import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { BooleanInput } from '@angular/cdk/coercion';
import { Subject, takeUntil } from 'rxjs';
import { CloudUser, UserStatus } from 'app/core/user/user.types';
import { UserService } from 'app/core/user/user.service';
import { AuthService } from 'app/core/auth/auth.service';
import { TranslocoService } from '@ngneat/transloco';
import { SwPush } from '@angular/service-worker';
import { PushService } from 'app/core/solar/push.service';
import { environment } from 'environments/environment';
import { ToastrService } from 'ngx-toastr';
import { CloudUserService } from 'app/core/user/cloud.user.service';

@Component({
    selector       : 'user',
    templateUrl    : './user.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'user'
})
export class UserComponent implements OnInit, OnDestroy
{
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = false;

    public isNotificationActive: boolean = false;
    public currentUser: CloudUser;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        public swPush: SwPush,
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _authService: AuthService,
        private _pushService: PushService,
        private _translateService: TranslocoService,
        private toastr: ToastrService
    )
    {
        this.swPush.notificationClicks.subscribe(({action, notification}) => {
            console.log('action', action, 'notification', notification);
        });

        this.swPush.messages.subscribe((payload)=> {
            console.log('message payload', payload);
        })
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        this._userService.cloudUser$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: CloudUser) => {
                // if (this.currentUser !== undefined) return;

                this.currentUser = user;

                if (this.currentUser.status !== UserStatus.online) {
                    this.updateUserStatus(UserStatus.online);
                }

                console.log("user-component ngOnInit cloudUser", user);
                if (this.swPush.isEnabled) {
                    if (user.subscription) {
                        this.isNotificationActive = true;
                    } else {
                        this.isNotificationActive = false;
                    }                
                }
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void
    {
        // Return if user is not available
        if ( !this.currentUser )
        {
            return;
        }

        // Update the user
        this._userService.update({
            ...this.currentUser,
            status
        }).subscribe();
    }

    enableNotifications(): void {
        
        this.swPush.requestSubscription({
            serverPublicKey: environment.vapidPublicKey
        })
        .then(sub => {
            console.log('sub', sub.toJSON());
            this._pushService.registerSubscription(sub.toJSON(), this._authService.currentCloudUser.station)

            this.currentUser.subscription = JSON.stringify(sub.toJSON());
            this.isNotificationActive = true;
            this.toastr.success('Subscribed successfully', 'Push Notification');
        })
        .catch(err => {
            console.error("Could not subscribe to notifications", err);
            this.toastr.error('Register error', 'Push Notification');
        });
    }

    disableNotifications(): void {
        this.swPush.unsubscribe().then(() => {
            this._pushService.unRegisterSubscription().then((result) => {
                console.log("disable notification success");
                this.currentUser.subscription = null;
                this.isNotificationActive = false;
                this.toastr.success('Unsubscribed successfully', 'Push Notification');
            })
            .catch((err) => {
                console.error("Error unregister notification", err);
                this.toastr.error('Unregistered error', 'Push Notification');
            });
        })
        .catch((err) => {
            console.error("Error unsubscribe notification", err);
            this.toastr.error('Unsubscribe error', 'Push Notification');
    })

    }

    showSettings(): void
    {
        const navExtras: NavigationExtras = {
            queryParams: {
            },
            state: {
            }
        };
        
        console.log("showSettings userRole", this.currentUser.userRole);
        if (this.currentUser.userRole == "Owner") {
            this._router.navigate(['/admin/settings'], navExtras);
        } else {
            this._router.navigate(['/settings'], navExtras);
        }
    }

    /**
     * Sign out
     */
    signOut(): void
    {
        // save active language before calling signout for a federated login user type
        this._authService.activeLanguage = this._translateService.getActiveLang();

        // update user status to sign-out
        this.updateUserStatus(UserStatus.signOut);

        if (this.currentUser.type) { // social login user type
            this._authService.signOut();
        }
        else {
            this._router.navigate(['/sign-out']);
        }
    }
}
